/** @jsxImportSource react */
import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { api } from '../services/api';
import { useTranslation } from '../i18n';
import { Loader2, CheckCircle, XCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';

const StatusCard: React.FC<{ icon: React.ReactNode; title: string; children: React.ReactNode; }> = ({ icon, title, children }) => (
    <div className="relative z-10 w-full max-w-lg mx-auto bg-white p-8 sm:p-12 shadow-2xl rounded-xl border border-secondary-100 animate-fade-in">
        <div className="text-center">
            <div className="flex justify-center mb-6">{icon}</div>
            <h2 className="text-3xl font-bold text-secondary-900 mb-4">{title}</h2>
            <div className="text-secondary-600 space-y-4">{children}</div>
        </div>
    </div>
);

const VerifyEmailPage = () => {
    const [searchParams] = useSearchParams();
    const { t } = useTranslation();
    const [status, setStatus] = useState('verifying'); // 'verifying', 'success', 'error'
    const [errorMessage, setErrorMessage] = useState('');

    useEffect(() => {
        const mode = searchParams.get('mode');
        const oobCode = searchParams.get('oobCode');

        if (mode === 'verifyEmail' && oobCode) {
            api.applyActionCode(oobCode)
                .then(() => {
                    setStatus('success');
                })
                .catch((error) => {
                    console.error("Verification error:", error);
                    setStatus('error');
                    setErrorMessage(t('invalidOrExpiredLink'));
                });
        } else {
            setStatus('error');
            setErrorMessage(t('invalidLink'));
        }
    }, [searchParams, t]);

    const renderContent = () => {
        switch (status) {
            case 'verifying':
                return (
                    <StatusCard
                        icon={<Loader2 className="w-16 h-16 text-primary-500 animate-spin" />}
                        title={t('verifyingEmail')}
                    >
                        <p>{t('pleaseWait')}</p>
                    </StatusCard>
                );
            case 'success':
                return (
                    <StatusCard
                        icon={<CheckCircle className="w-16 h-16 text-emerald-500" />}
                        title={t('emailVerifiedSuccess')}
                    >
                        <p>{t('emailVerifiedSuccessDesc')}</p>
                        <Link to="/login">
                            <Button className="mt-6 w-full">{t('goToLogin')}</Button>
                        </Link>
                    </StatusCard>
                );
            case 'error':
                return (
                    <StatusCard
                        icon={<XCircle className="w-16 h-16 text-rose-500" />}
                        title={t('verificationFailed')}
                    >
                        <p>{errorMessage}</p>
                        <p>{t('invalidOrExpiredLinkDesc')}</p>
                         <Link to="/login">
                            <Button variant="outline" className="mt-6 w-full">{t('backToLogin')}</Button>
                        </Link>
                    </StatusCard>
                );
            default:
                return null;
        }
    };

    return (
        <div className="relative min-h-screen w-full bg-secondary-50 flex items-center justify-center p-4 overflow-hidden">
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-primary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            <div className="absolute -bottom-24 -right-24 w-96 h-96 bg-secondary-500/10 rounded-full mix-blend-multiply filter blur-3xl opacity-50"></div>
            {renderContent()}
        </div>
    );
};

export default VerifyEmailPage;